"use client"

import * as React from "react"

import { AskLeoComposer } from "@/components/ask-leo-composer"
import { Button } from "@exxatdesignux/ui/components/button"
import { Kbd } from "@/components/ui/kbd"
import { LeoIcon } from "@/components/ui/leo-icon"
import { Tip } from "@/components/ui/tip"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import { withLeoBankOnlyInstruction } from "@/lib/leo-section-ask"
import { cn } from "@/lib/utils"

export type LeoAskChoice = {
  id: string
  label: string
  description?: string
  instruction: string
}

const CHOICE_LETTERS = "ABCDEFGHIJ"

export function LeoAskPrompt({
  question,
  choices,
  disabled = false,
  className,
  onSubmit,
  onClose,
  accessory,
  showBankOnlyToggle = false,
  appendBankOnly = true,
}: {
  question: string
  choices: readonly LeoAskChoice[]
  disabled?: boolean
  className?: string
  onSubmit: (instruction: string) => void
  onClose?: () => void
  accessory?: React.ReactNode
  showBankOnlyToggle?: boolean
  /** Paper fill keeps the bank suffix. Compose Ask Leo must not. */
  appendBankOnly?: boolean
}) {
  const bankOnlyId = React.useId()
  const bankOnlyLabelId = `${bankOnlyId}-label`
  const [custom, setCustom] = React.useState("")
  const [bankOnly, setBankOnly] = React.useState(true)
  const startedRef = React.useRef(false)

  const start = React.useCallback(
    (instruction: string) => {
      const next = withLeoBankOnlyInstruction(
        instruction,
        appendBankOnly && bankOnly,
      )
      if (disabled || startedRef.current || !next) return
      startedRef.current = true
      onSubmit(next)
    },
    [appendBankOnly, bankOnly, disabled, onSubmit],
  )

  React.useEffect(() => {
    if (disabled) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return
      const target = event.target
      if (
        target instanceof HTMLElement &&
        target.closest("textarea, input, [contenteditable='true']")
      ) {
        return
      }
      const index = CHOICE_LETTERS.indexOf(event.key.toUpperCase())
      const choice = index >= 0 ? choices[index] : undefined
      if (!choice) return
      event.preventDefault()
      start(choice.instruction)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [choices, disabled, start])

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex items-start gap-2.5 px-3.5 pt-3.5">
        <span
          className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand"
          aria-hidden
        >
          <LeoIcon variant="ambient" size="sm" state="invited" />
        </span>
        <p className="min-w-0 flex-1 pt-0.5 text-sm font-medium leading-snug text-foreground">
          {question}
        </p>
        {onClose ? (
          <Tip label="Close">
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              disabled={disabled}
              aria-label="Close"
              onClick={onClose}
              className="shrink-0"
            >
              <i className="fa-light fa-xmark" aria-hidden="true" />
            </Button>
          </Tip>
        ) : null}
      </div>
      <div role="group" aria-label={question} className="mt-2 flex flex-col gap-0.5 px-2">
        {choices.map((choice, index) => {
          const letter = CHOICE_LETTERS[index] ?? String(index + 1)
          return (
            <button
              key={choice.id}
              type="button"
              disabled={disabled}
              aria-label={`${letter}. ${choice.label}`}
              onClick={() => start(choice.instruction)}
              className="flex w-full items-start gap-2.5 rounded-2 px-2.5 py-2 text-start transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            >
              <Kbd variant="bare" className="mt-0.5">
                {letter}
              </Kbd>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium leading-snug text-foreground">
                  {choice.label}
                </span>
                {choice.description ? (
                  <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                    {choice.description}
                  </span>
                ) : null}
              </span>
            </button>
          )
        })}
      </div>
      {showBankOnlyToggle ? (
        <div className="mt-1 border-t border-border">
          <label
            htmlFor={bankOnlyId}
            className="flex cursor-pointer items-center justify-between gap-3 px-3.5 py-2.5"
          >
            <span
              id={bankOnlyLabelId}
              className="min-w-0 text-sm font-medium text-foreground"
            >
              Only pick from your bank
            </span>
            <ToggleSwitch
              id={bankOnlyId}
              aria-labelledby={bankOnlyLabelId}
              checked={bankOnly}
              disabled={disabled}
              onChange={setBankOnly}
            />
          </label>
        </div>
      ) : null}
      {accessory ? (
        <div className={showBankOnlyToggle ? "border-t border-border" : "mt-1 border-t border-border"}>
          {accessory}
        </div>
      ) : null}
      <div className="border-t border-border px-3 py-3">
        <AskLeoComposer
          value={custom}
          onChange={setCustom}
          onSubmit={start}
          placeholder="Describe what you want"
          inputLabel="Describe what you want"
          submitButtonAriaLabel="Ask Leo"
          leadingSlot="ai-mark"
          size="sm"
          dictationDisabled
          composerShellClassName="rounded-[25px]! border-[color:var(--control-border)]! bg-card! shadow-none!"
        />
      </div>
    </div>
  )
}
